import React, { useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTranslation } from 'react-i18next';

import { OrganizationIcon } from '~/assets';
import { ShimmerText } from '~/components/Shimmer';
import { ConversationReference, ConversationReferenceType } from '~/types/api.graphql';
import { conditionIcon } from '~/utils/audience';

import {
  ConversationsPopoverGradient,
  MentionDropdown,
  MentionDropdownEmpty,
  MentionDropdownItem,
  MentionDropdownItemIcon,
  MentionDropdownItemName,
  MentionDropdownScrollList,
  StyledEditorDiv,
} from './AIInsightsSidebar.styles';
import { truncateName } from './mentionChipUtils';

interface AudienceMentionInputProps {
  editorRef: React.Ref<HTMLDivElement>;
  onInput: () => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLDivElement>) => void;
  onCopy: (e: React.ClipboardEvent<HTMLDivElement>) => void;
  onCut: (e: React.ClipboardEvent<HTMLDivElement>) => void;
  onPaste: (e: React.ClipboardEvent<HTMLDivElement>) => void;
  onMouseDown: (e: React.MouseEvent<HTMLDivElement>) => void;
  placeholder: string;
  disabled: boolean;
  mentionQuery: string | null;
  mentionResults: ConversationReference[];
  isLoadingMentions: boolean;
  onSelectMention: (ref: ConversationReference) => void;
  onCloseMention: () => void;
}

const MentionItemIcon = ({ item }: { item: ConversationReference }) => {
  if (item.type === ConversationReferenceType.Organization) {
    return <OrganizationIcon />;
  }
  if (item.audienceCondition) {
    return <>{conditionIcon(item.audienceCondition)}</>;
  }
  return null;
};

const AudienceMentionInput = ({
  editorRef,
  onInput,
  onKeyDown,
  onCopy,
  onCut,
  onPaste,
  onMouseDown: onEditorMouseDown,
  placeholder,
  disabled,
  mentionQuery,
  mentionResults,
  isLoadingMentions,
  onSelectMention,
  onCloseMention,
}: AudienceMentionInputProps) => {
  const { t } = useTranslation();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const scrollListRef = useRef<HTMLDivElement>(null);
  const [dropdownStyle, setDropdownStyle] = useState<{ top: number; left: number; width: number } | null>(null);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [showTopGradient, setShowTopGradient] = useState(false);
  const [showBottomGradient, setShowBottomGradient] = useState(false);

  const updateGradients = useCallback(() => {
    const el = scrollListRef.current;
    if (!el) return;
    setShowTopGradient(el.scrollTop > 0);
    setShowBottomGradient(el.scrollTop + el.clientHeight < el.scrollHeight - 1);
  }, []);

  // Reset highlight when results change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [mentionResults]);

  useEffect(() => {
    requestAnimationFrame(() => requestAnimationFrame(updateGradients));
  }, [mentionResults, updateGradients]);

  useEffect(() => {
    if (mentionQuery === null) {
      setShowTopGradient(false);
      setShowBottomGradient(false);
    }
  }, [mentionQuery]);

  // Compute dropdown position above the wrapper whenever mentionQuery becomes active
  useEffect(() => {
    if (mentionQuery === null || !wrapperRef.current) {
      setDropdownStyle(null);
      return;
    }
    const rect = wrapperRef.current.getBoundingClientRect();
    // Position dropdown above the input wrapper
    setDropdownStyle({
      top: rect.top, // will be translated upward via transform in the styled component
      left: rect.left,
      width: rect.width,
    });
  }, [mentionQuery]);

  // Recompute when window resizes
  useEffect(() => {
    if (mentionQuery === null) return undefined;
    const update = () => {
      if (!wrapperRef.current) return;
      const rect = wrapperRef.current.getBoundingClientRect();
      setDropdownStyle({ top: rect.top, left: rect.left, width: rect.width });
    };
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [mentionQuery]);

  const isMentionActive = mentionQuery !== null;
  const isDropdownOpen = isMentionActive;
  const queryIsLong = mentionQuery !== null && mentionQuery.trim().length >= 2;
  const showTypeToSearch = isDropdownOpen && !queryIsLong;
  const showLoading = isDropdownOpen && queryIsLong && isLoadingMentions;
  const showEmpty = isDropdownOpen && queryIsLong && !isLoadingMentions && mentionResults.length === 0;

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (isDropdownOpen) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setHighlightedIndex((i: number) => Math.min(i + 1, mentionResults.length - 1));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setHighlightedIndex((i) => Math.max(i - 1, 0));
          return;
        }
      }
      onKeyDown(e);
    },
    [isDropdownOpen, mentionResults.length, onKeyDown],
  );

  const handleSelectMention = useCallback(
    (ref: ConversationReference) => {
      onSelectMention(ref);
      setHighlightedIndex(0);
    },
    [onSelectMention],
  );

  const renderDropdown = () => {
    if (!isDropdownOpen || !dropdownStyle) return null;
    return ReactDOM.createPortal(
      <MentionDropdown
        $top={dropdownStyle.top}
        $left={dropdownStyle.left}
        style={{
          width: dropdownStyle.width,
          transform: 'translateY(-100%)',
          marginTop: '-0.25rem',
        }}
        onMouseDown={(e) => e.preventDefault()}
        onTouchStart={(e) => e.preventDefault()}
      >
        <ConversationsPopoverGradient $visible={showTopGradient} $position="top" />
        <MentionDropdownScrollList ref={scrollListRef} onScroll={updateGradients}>
          {showTypeToSearch ? (
            <MentionDropdownEmpty>{t('aiInsights.typeToSearch')}</MentionDropdownEmpty>
          ) : showLoading ? (
            <>
              {[...Array(3)].map((_, i) => (
                <ShimmerText key={i} width="100%" height="2rem" marginBottom="0.125rem" />
              ))}
            </>
          ) : showEmpty ? (
            <MentionDropdownEmpty>{t('aiInsights.noResults')}</MentionDropdownEmpty>
          ) : (
            mentionResults.map((item, idx) => (
              <MentionDropdownItem
                key={item.deepLink}
                type="button"
                $highlighted={idx === highlightedIndex}
                onMouseEnter={() => setHighlightedIndex(idx)}
                onMouseDown={(e) => {
                  e.preventDefault();
                  handleSelectMention(item);
                }}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  handleSelectMention(item);
                }}
              >
                <MentionDropdownItemIcon>
                  <MentionItemIcon item={item} />
                </MentionDropdownItemIcon>
                <MentionDropdownItemName title={item.name}>{truncateName(item.name)}</MentionDropdownItemName>
              </MentionDropdownItem>
            ))
          )}
        </MentionDropdownScrollList>
        <ConversationsPopoverGradient $visible={showBottomGradient} $position="bottom" />
      </MentionDropdown>,
      document.body,
    );
  };

  // Expose highlighted index for parent's Enter/Tab handling
  // We handle it internally here via onKeyDown override
  const handleKeyDownWithSelect = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (isDropdownOpen && (e.key === 'Enter' || e.key === 'Tab') && mentionResults.length > 0) {
        e.preventDefault();
        handleSelectMention(mentionResults[highlightedIndex]);
        return;
      }
      if (isMentionActive && e.key === 'Escape') {
        e.preventDefault();
        onCloseMention();
        return;
      }
      handleKeyDown(e);
    },
    [
      isMentionActive,
      isDropdownOpen,
      mentionResults,
      highlightedIndex,
      handleSelectMention,
      onCloseMention,
      handleKeyDown,
    ],
  );

  return (
    <div ref={wrapperRef} style={{ flex: 1, display: 'flex', minHeight: '3rem', position: 'relative' }}>
      <StyledEditorDiv
        ref={editorRef}
        contentEditable={!disabled}
        suppressContentEditableWarning
        role="textbox"
        aria-multiline="true"
        data-placeholder={placeholder}
        data-mention-pending={isMentionActive ? 'true' : undefined}
        onInput={onInput}
        onKeyDown={handleKeyDownWithSelect}
        onCopy={onCopy}
        onCut={onCut}
        onPaste={onPaste}
        onMouseDown={onEditorMouseDown}
      />
      {renderDropdown()}
    </div>
  );
};

export default AudienceMentionInput;
